import { Button, FullPageStatus } from 'corso-admin-ui';
import { DbJsonSchema } from 'corso-types';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  Navigate,
  NavLink,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { z } from 'zod';
import { identifyUser } from '~/utils/observability';
import { useUser } from './UserProvider';

type User = ReturnType<typeof useUser>;
type StoreUser = User['storeUserRoles'][number];

const allDisabled = DbJsonSchema.Store.featureFlags.parse({});

const StoreUserContext = createContext<null | {
  storeUser: StoreUser;
  // TODO might be nicer to have this accept the same response type as the `useStoreId` hook
  changeStoreUser: (storeId: StoreUser['storeId']) => void;
}>(null);

const lastUsedStoreIdKey = 'last-used-store-id';

const storeIdParamsSchema = z.object({ storeId: z.templateLiteral([z.int()]) });

function NoStoreAccess({ email }: { email: string }) {
  return (
    <FullPageStatus
      title="No Store Access"
      details={`Your user, ${email}, does not have access to any stores. You can be added by invitation from an admin.`}
    >
      <Button variant="primary" asChild size="lg">
        <NavLink to="/sign-out">Log Out</NavLink>
      </Button>
    </FullPageStatus>
  );
}

/** Redirects a path without a `storeId` to the same path under the last used store, falling back to the first store. */
export function DefaultStoreRedirect() {
  const user = useUser();
  const location = useLocation();
  const [lastUsedStoreId] = useLocalStorage<number | null>(
    lastUsedStoreIdKey,
    null,
  );

  const [fallbackStoreUser] = user.storeUserRoles;
  const lastUsedStoreUser = user.storeUserRoles.find(
    (sur) => sur.storeId === lastUsedStoreId,
  );

  const storeUser = lastUsedStoreUser ?? fallbackStoreUser;

  if (!storeUser) return <NoStoreAccess email={user.email} />;

  return (
    <Navigate
      to={`/${storeUser.storeId}${location.pathname.replace(/\/$/, '')}`}
      replace
    />
  );
}

function useStoreUserContext() {
  const context = useContext(StoreUserContext);
  if (!context) {
    throw new Error(
      'StoreUserContext unavailable. Did you forget to use a StoreUserProvider?',
    );
  }
  return context;
}

/** Provides the currently active `StoreUser` of the `User`. */
export function useStoreUser() {
  const { storeUser } = useStoreUserContext();
  return storeUser;
}

/** Provides the `storeId` of the currently active `StoreUser` within a literal string, suitable for use as a path parameter for most API calls. */
export function useStoreId() {
  const storeUser = useStoreUser();
  return `${storeUser.storeId}` as const;
}

/** Store-level feature flags; a `/user` response predating a flag reads as disabled. */
export function useStoreFeatureFlags() {
  const { storeUser } = useStoreUserContext();
  // the response is never parsed, so a stale cache or an older API can omit these
  return (
    DbJsonSchema.Store.featureFlags.safeParse(storeUser.store.featureFlags)
      .data ?? allDisabled
  );
}

export function useChangeStoreUser() {
  const { changeStoreUser } = useStoreUserContext();
  return changeStoreUser;
}

/** Provides the `StoreUser` of the `User` for the `storeId` path parameter. */
export function StoreUserProvider({ children }: PropsWithChildren) {
  const user = useUser();
  const params = useParams();
  const [, setLastUsedStoreId] = useLocalStorage<number | null>(
    lastUsedStoreIdKey,
    null,
  );

  const parsed = storeIdParamsSchema.safeParse(params);
  const pathStoreId = parsed.data?.storeId;

  // compared as strings, so a non-canonical id such as `010` matches no store
  const storeUser = user.storeUserRoles.find(
    (sur) => `${sur.storeId}` === pathStoreId,
  );
  const storeUserId = storeUser?.storeId;
  const storeName = storeUser?.store.name;

  const navigate = useNavigate();

  const changeStoreUser = useCallback(
    (storeId: StoreUser['storeId']) => {
      if (storeId === storeUserId) return;
      navigate(`/${storeId}`);
    },
    [navigate, storeUserId],
  );

  const value = useMemo(() => {
    if (!storeUser) return null;
    return { storeUser, changeStoreUser } as const;
  }, [storeUser, changeStoreUser]);

  useEffect(() => {
    if (storeUserId === undefined) return;
    setLastUsedStoreId(storeUserId);
    identifyUser({ email: user.email, storeId: storeUserId, storeName });
  }, [setLastUsedStoreId, storeUserId, storeName, user.email]);

  // a segment that isn't an integer is a path that predates `storeId` being required, such as a widget integration link
  if (pathStoreId === undefined) return <DefaultStoreRedirect />;
  // an inaccessible store, so start over from the default
  if (!value) return <Navigate to="/" replace />;

  return (
    <StoreUserContext.Provider value={value}>
      {children}
    </StoreUserContext.Provider>
  );
}
